import React, { PureComponent, Fragment } from "react";
import { connect } from "react-redux";
import { withRouter, Route, matchPath } from "react-router-dom";
import { PropTypes } from "prop-types";

import SimplePreloader from "../../components/preloaders/SimplePreloader.jsx";
import VerifyUser from "../sentry/VerifyUser.jsx";
import Sentry from "../sentry/Sentry.jsx";
import Console from "../console/Console.jsx";
import ErrorModal from "../../components/modals/ErrorModal.jsx";

import { ROUTES } from "../../constants/routing.js";

import { dismissError } from "../../state/reducers/api/apiActions.js";

import { selectLatestErrorMessage } from "../../state/reducers/api/apiSelectors.js";
import { selectCopy } from "../../state/reducers/locale/copySelector.js";

class App extends PureComponent {
  static propTypes = {
    localeIsReady: PropTypes.bool.isRequired,
    isAuthenticated: PropTypes.bool.isRequired
  };

  componentDidMount() {
    // general state listeners
    window.addEventListener("resize", () => {});
    window.addEventListener("orientationchange", () => {});
    window.addEventListener("blur", () => {});
  }

  // -- HANDLERS -- //

  closeErrorModal = () => {
    this.props.dismissError();
  };

  // -- RENDER -- //

  renderWithErrorModal = Content => {
    const { showError, error, copy } = this.props;

    // manually override error copy for specific errors
    // not the nicest implementation but it avoids poluting the error flow with copy data
    // 408 error should be handled with a notification in future
    const errorsToManage = [408];
    let managedError;
    if (error && errorsToManage.includes(error.status)) {
      managedError = {
        ...error,
        title: copy(`${error.status}_error.title`),
        message: copy(`${error.status}_error.body`)
      };
    }

    return (
      <Fragment>
        <Content />
        {showError === true && error && (
          <ErrorModal
            open={true}
            onClose={!error.isFatal ? this.closeErrorModal : null}
            error={managedError || error}
            copy={copy("errors")}
          />
        )}
      </Fragment>
    );
  };

  renderFatalErrorModal = copyKey => {
    const { error, copy } = this.props;

    const fatalError = {
      ...error,
      title: copy(`${copyKey}.title`),
      message: copy(`${copyKey}.body`)
    };

    return <ErrorModal open={true} error={fatalError} copy={copy("errors")} />;
  };

  render() {
    const {
      apiIsError,
      configIsError,
      configIsReady,
      localeIsReady,
      endpointsIsError,
      endpointsIsReady,
      authIsReady,
      isAuthenticated,
      coreIsReady,
      location,
      error
    } = this.props;

    // disable app if config is unavailable
    // locale with use bundled fallback.json if load fails
    if (configIsError === true) {
      return this.renderFatalErrorModal("config_error");
    }

    if (endpointsIsError === true) {
      return this.renderFatalErrorModal("endpoints_error");
    }

    // full page error message for 503 responses
    if (apiIsError && error && error.status === 503) {
      return this.renderFatalErrorModal("503_error");
    }

    // loading spinner
    if (
      localeIsReady === false ||
      configIsReady === false ||
      endpointsIsReady === false ||
      authIsReady === false ||
      (isAuthenticated === true && coreIsReady === false)
    ) {
      return this.renderWithErrorModal(SimplePreloader);
    }

    // "verify" operates outside of both Sentry and Console
    const verify = matchPath(location.pathname, {
      path: `${ROUTES.VERIFY}/:token`
    });
    if (verify) {
      return <Route path={`${ROUTES.VERIFY}/:token`} component={VerifyUser} />;
    }

    // core loading result - error or Console
    if (isAuthenticated === true) {
      if (apiIsError === true && coreIsReady === false) {
        return this.renderFatalErrorModal("core_error");
      }
      return this.renderWithErrorModal(Console);
    }

    // if not in an error state and not authorised, show the Sentry
    return this.renderWithErrorModal(Sentry);
  }
}

const mapStateToProps = state => ({
  configIsError: state.config.isError,
  endpointsIsError: state.endpoints.isErrored,
  configIsReady: state.config.isReady,
  localeIsReady: state.locale.isReady,
  endpointsIsReady: state.endpoints.isReady,
  authIsReady: state.authentication.isReady,
  apiIsError: state.api.isError,
  coreIsReady: state.core.isReady,
  isAuthenticated: state.authentication.isAuthenticated,
  showError: state.api.showError,
  error: selectLatestErrorMessage(state),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  dismissError: () => dispatch(dismissError())
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(App));
